import { useMemo, useState, useRef } from "preact/hooks"
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps"
import { DcTotalPoint } from "../sales_forecasting/types/distributor_sales"

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"

// Keyed by UPPER(TRIM(dc_name)) — matches backend normalization
const KEHE_DC_COORDS: Record<string, [number, number]> = {
  "AURORA, CO":          [-104.83, 39.73],
  "BLOOMINGTON, IN":     [-86.52,  40.48],
  "ELLETTSVILLE, IN":    [-86.63,  39.23],
  "BREINIGSVILLE, PA":   [-75.63,  40.61],
  "LEHIGH VALLEY, PA":   [-75.63,  40.61],
  "NORTH EAST, MD":      [-75.94,  39.60],
  "CHINO, CA":           [-117.69, 34.01],
  "CHINO A, CA":         [-117.69, 34.02],
  "CHINO B":             [-117.69, 34.00],
  "CHINO B, CA":         [-117.69, 34.00],
  "ONTARIO, CA":         [-117.65, 34.07],
  "STOCKTON, CA":        [-121.29, 37.96],
  "DALLAS, TX":          [-96.80,  32.78],
  "FLOWER MOUND, TX":    [-97.10,  33.03],
  "DOUGLASVILLE, GA":    [-84.75,  33.75],
  "ELKTON, FL":          [-81.63,  29.79],
  "ST. AUGUSTINE, FL":   [-81.31,  29.89],
  "FT. LAUDERDALE, FL":  [-80.15,  25.99],
  "HIALEAH, FL":         [-80.28,  25.86],
  "GOODYEAR, AZ":        [-112.36, 33.45],
  "PHOENIX, AZ":         [-112.07, 33.45],
  "PORTLAND, OR":        [-122.68, 45.52],
  "TUALATIN, OR":        [-122.76, 45.38],
  "ROMEOVILLE, IL":      [-88.09,  41.65],
}

export const DC_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#9333ea",
  "#0891b2", "#db2777", "#ea580c", "#4f46e5", "#059669",
  "#7c3aed", "#0284c7", "#be123c", "#65a30d",
]

export function formatAmount(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}

const SVG_WIDTH = 960
const SVG_HEIGHT = 520
const MIN_RADIUS = 7
const MAX_RADIUS = 36

interface PlacedMarker {
  dc_name: string
  coords: [number, number]
  total_amount: number
  invoice_count: number
  order_count: number
  avg_weeks_between_orders?: number | null
  avg_amount_per_order?: number | null
  radius: number
  color: string
}

interface TooltipState {
  marker: PlacedMarker
  x: number
  y: number
}

interface DcMapProps {
  data: DcTotalPoint[]
  class?: string
  className?: string
}

/** Bubble area ∝ dollar amount (sqrt scaling vs global max). */
function radiusForAmount(amount: number, maxAmount: number): number {
  if (maxAmount <= 0) return MIN_RADIUS
  const t = Math.sqrt(amount / maxAmount)
  return MIN_RADIUS + t * (MAX_RADIUS - MIN_RADIUS)
}

function coordKey(lng: number, lat: number): string {
  return `${lng.toFixed(3)},${lat.toFixed(3)}`
}

interface RawMarker {
  dc_name: string
  base: [number, number]
  total_amount: number
  invoice_count: number
  order_count: number
  avg_weeks_between_orders?: number | null
  avg_amount_per_order?: number | null
  radius: number
  color: string
}

/** Spread overlapping DCs that share the same lat/lng (e.g. Lehigh Valley / Breinigsville). */
function applyOverlapOffsets(markers: RawMarker[]): PlacedMarker[] {
  const groups = new Map<string, RawMarker[]>()
  for (const m of markers) {
    const k = coordKey(m.base[0], m.base[1])
    const g = groups.get(k) ?? []
    g.push(m)
    groups.set(k, g)
  }

  const out: PlacedMarker[] = []
  for (const group of groups.values()) {
    const n = group.length
    group.forEach((m: RawMarker, i: number) => {
      if (n === 1) {
        out.push({
          dc_name: m.dc_name,
          coords: m.base,
          total_amount: m.total_amount,
          invoice_count: m.invoice_count,
          order_count: m.order_count,
          avg_weeks_between_orders: m.avg_weeks_between_orders,
          avg_amount_per_order: m.avg_amount_per_order,
          radius: m.radius,
          color: m.color,
        })
        return
      }
      const angle = (i / n) * 2 * Math.PI + i * 0.4
      const spread = 0.52 * Math.sqrt(i + 1)
      const dLng = Math.cos(angle) * spread * 0.55
      const dLat = Math.sin(angle) * spread
      out.push({
        dc_name: m.dc_name,
        coords: [m.base[0] + dLng, m.base[1] + dLat],
        total_amount: m.total_amount,
        invoice_count: m.invoice_count,
        order_count: m.order_count,
        avg_weeks_between_orders: m.avg_weeks_between_orders,
        avg_amount_per_order: m.avg_amount_per_order,
        radius: m.radius,
        color: m.color,
      })
    })
  }
  return out.sort((a: PlacedMarker, b: PlacedMarker) => b.total_amount - a.total_amount)
}

export function DcMap({ data, class: classProp, className }: DcMapProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const markers = useMemo(() => {
    if (!data.length) return []
    const maxAmount = Math.max(...data.map((d: DcTotalPoint) => d.total_amount))

    const raw: RawMarker[] = data
      .map((d: DcTotalPoint, idx: number): RawMarker | null => {
        const key = d.dc_name.toUpperCase().trim()
        const base = KEHE_DC_COORDS[key]
        if (!base) return null
        return {
          dc_name: d.dc_name,
          base,
          total_amount: d.total_amount,
          invoice_count: d.invoice_count,
          order_count: d.order_count,
          avg_weeks_between_orders: d.avg_weeks_between_orders,
          avg_amount_per_order: d.avg_amount_per_order,
          radius: radiusForAmount(d.total_amount, maxAmount),
          color: DC_COLORS[idx % DC_COLORS.length],
        }
      })
      .filter((m): m is RawMarker => m !== null)

    return applyOverlapOffsets(raw)
  }, [data])

  function handleMouseEnter(marker: PlacedMarker, e: MouseEvent) {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setTooltip({ marker, x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  function handleMouseMove(marker: PlacedMarker, e: MouseEvent) {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setTooltip({ marker, x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  function handleMouseLeave() {
    setTooltip(null)
  }

  const containerClass = `relative w-full ${classProp || className || ""}`

  return (
    <div>
      <div ref={containerRef} class={containerClass} style={{ background: "#f8fafc", borderRadius: "0.5rem" }}>
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1120 }}
          width={SVG_WIDTH}
          height={SVG_HEIGHT}
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }: { geographies: any[] }) =>
              geographies.map((geo: any) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e2e8f0"
                  stroke="#cbd5e1"
                  strokeWidth={0.5}
                  style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                />
              ))
            }
          </Geographies>

          {markers.map((marker: PlacedMarker) => (
            <Marker key={marker.dc_name} coordinates={marker.coords}>
              <circle
                r={marker.radius}
                fill={marker.color}
                fillOpacity={0.78}
                stroke="white"
                strokeWidth={1.5}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e: any) => handleMouseEnter(marker, e)}
                onMouseMove={(e: any) => handleMouseMove(marker, e)}
                onMouseLeave={handleMouseLeave}
              />
            </Marker>
          ))}
        </ComposableMap>

        {tooltip && (
          <div
            class="absolute bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs pointer-events-none z-10 min-w-[150px]"
            style={{ left: tooltip.x + 14, top: tooltip.y - 14 }}
          >
            <div class="font-semibold text-gray-800 mb-1.5">{tooltip.marker.dc_name}</div>
            <div class="flex items-center justify-between gap-4">
              <span class="text-gray-500">Total</span>
              <span class="font-medium text-gray-900">{formatAmount(tooltip.marker.total_amount)}</span>
            </div>
            <div class="flex items-center justify-between gap-4">
              <span class="text-gray-500">Invoices</span>
              <span class="font-medium text-gray-900">{tooltip.marker.invoice_count.toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between gap-4">
              <span class="text-gray-500">POs</span>
              <span class="font-medium text-gray-900">{tooltip.marker.order_count.toLocaleString()}</span>
            </div>
            <div class="flex items-center justify-between gap-4">
              <span class="text-gray-500">Avg gap</span>
              <span class="font-medium text-gray-900">{formatWeeks(tooltip.marker.avg_weeks_between_orders)}</span>
            </div>
            <div class="flex items-center justify-between gap-4">
              <span class="text-gray-500">Avg / PO</span>
              <span class="font-medium text-gray-900">{formatOptionalAmount(tooltip.marker.avg_amount_per_order)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Legend: avoids overlapping text on the map */}
      {markers.length > 0 && (
        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-700 px-1">
          {markers.map((m: PlacedMarker) => (
            <div key={m.dc_name} class="flex items-center gap-1.5 min-w-0">
              <span class="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: m.color }} />
              <span class="font-medium truncate max-w-[140px]" title={m.dc_name}>{m.dc_name}</span>
              <span class="text-gray-500">{formatAmount(m.total_amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function formatWeeks(value: number | null | undefined): string {
  if (value == null) return "—"
  return `${value.toFixed(value >= 10 ? 0 : 1)} wks`
}

function formatOptionalAmount(value: number | null | undefined): string {
  if (value == null) return "—"
  return formatAmount(value)
}
